// Pure helper for the New Purchase Order page's product search + "Select
// product" dropdown.
//
// GitHub issue #60 — the product search field and "Select product" dropdown
// never showed any products on /inventory/purchase-orders/new (Step 2 — Add
// Items). Two problems compounded:
//   1. The products fetch was gated on the chosen receiving location
//      (`enabled: Boolean(locationId)`), and
//   2. it was *scoped* to that location (`location_id: locationId`).
// Products are location-scoped rows in the catalog, so a Purchase Order — which
// exists precisely to bring NEW stock into a location — would find no products
// whenever the tenant's catalog lives under a different location than the
// selected receiving location, leaving the dropdown and search permanently
// empty. The issue's Expected Behaviour asks for "all available products for
// the tenant", so the PO product picker must NOT scope by the receiving
// location. Same root-cause family as the already-fixed issue #57 (New Stock
// Transfer page): keep the query logic in one testable place and always fetch
// the full active catalog.

/**
 * Query params for the product list shown in the purchase-order item dropdown.
 *
 * The receiving location is deliberately NOT used to scope the product list: a
 * purchase order brings new stock into a location, so every active product in
 * the tenant catalog must be selectable regardless of where it is currently
 * stocked. Only the typed search term (trimmed) narrows the list.
 *
 * A variant parent is a grouping placeholder, not a stockable item: its units
 * live on its children and the POS can only sell a specific variant. Ordering
 * or receiving against the parent SKU strands the stock on a row nothing can
 * sell. So the dropdown lists the *leaves* of the catalog — standalone products
 * plus variant children — by asking for variants (`include_variants`) while
 * excluding anything that has children of its own (`has_variants: 'false'`).
 * Without `include_variants` the list defaults to top-level rows only, which
 * would drop every variant and leave the parents as the only choice.
 */
export function buildProductSearchParams(productSearch: string): {
  search?: string;
  limit: number;
  is_active: string;
  include_variants: string;
  has_variants: string;
} {
  return {
    search: productSearch.trim() || undefined,
    limit: 50,
    is_active: 'true',
    include_variants: 'true',
    has_variants: 'false',
  };
}
